import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { CreditCardIcon, BuildingLibraryIcon, BanknotesIcon, GiftIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { getBillingDetailsApi, addGiftCardApi } from '../../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn } from '../../../utils/commonUtil';
import { titleCaseFormatter } from '../../../utils/stringFormatters';

// Components
import Stripe from '../../../components/Stripe';
import { Section, LinkButton, Placeholder, LoadingRow } from './ui';
import { SECTION_IDS, isOk } from './helpers';

export const isPaymentValid = (details) => {
  const type = details?.billing_details?.default_payment_method_type;
  if (type === 'ACH' || type === 'CHECK') return true;
  return type === 'STRIPE' && !!details?.billing_details?.default_payment_method;
};

export const paymentLabel = (details) => {
  const bd = details?.billing_details;
  switch (bd?.default_payment_method_type) {
    case 'STRIPE':
      return bd?.default_payment_method
        ? `${titleCaseFormatter(bd.default_payment_method.card_brand)} •••• ${bd.default_payment_method.card_last4}`
        : '';
    case 'ACH':
      return 'Bank account (ACH)';
    case 'CHECK':
      return 'Pay by check';
    default:
      return '';
  }
};

export default function PaymentSection() {
  const dispatch = useDispatch();
  const { orderServiceAddress, orderPaymentDetails } = useSelector((s) => s.createNewOrder);
  const [loading, setLoading] = useState(false);
  const [cardFormOpen, setCardFormOpen] = useState(false);
  const [giftBalance, setGiftBalance] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [giftError, setGiftError] = useState(null);
  const [giftSaving, setGiftSaving] = useState(false);

  const addressId = orderServiceAddress?.id;

  const fetchBilling = () => {
    if (!addressId) return;
    setLoading(true);
    getBillingDetailsApi(addressId).then((res) => {
      setLoading(false);
      if (isOk(res)) {
        const det = res?.data?.addresses?.find((a) => a.id === addressId) || null;
        dispatch(setOrderDataAction({ orderPaymentDetails: det }));
        setGiftBalance(res?.data?.available_gift_card_balance || null);
        const bd = det?.billing_details;
        setCardFormOpen(bd?.default_payment_method_type === 'STRIPE' && !bd?.default_payment_method);
      } else toast.error(res?.message || 'Something went wrong');
    });
  };

  useEffect(fetchBilling, [addressId]);

  const redeemGiftCard = () => {
    if (!giftCode.trim()) {
      setGiftError('Enter a gift card code');
      return;
    }
    setGiftSaving(true);
    addGiftCardApi({ gift_card_code: giftCode.trim() }).then((res) => {
      setGiftSaving(false);
      if (isOk(res)) {
        toast.success(res?.message || 'Gift card added');
        setGiftCode('');
        setGiftError(null);
        setGiftOpen(false);
        fetchBilling();
      } else setGiftError(res?.message || 'Invalid gift card');
    });
  };

  const type = orderPaymentDetails?.billing_details?.default_payment_method_type;
  const valid = isPaymentValid(orderPaymentDetails);
  const Icon = type === 'ACH' ? BuildingLibraryIcon : type === 'CHECK' ? BanknotesIcon : CreditCardIcon;
  const disabled = !addressId;

  return (
    <Section
      id={SECTION_IDS.payment}
      step={4}
      title='Payment'
      summary={paymentLabel(orderPaymentDetails) || null}
      done={valid && !cardFormOpen}
      disabled={disabled}
      action={
        !disabled &&
        !loading &&
        type === 'STRIPE' &&
        valid && (
          <LinkButton onClick={() => setCardFormOpen((v) => !v)}>{cardFormOpen ? 'Cancel' : 'Change card'}</LinkButton>
        )
      }
    >
      {disabled ? (
        <Placeholder>Choose a pickup address to see payment options.</Placeholder>
      ) : loading ? (
        <LoadingRow text='Loading payment details…' />
      ) : (
        <div className='space-y-3'>
          {valid && (
            <div className='flex items-center gap-3'>
              <div className='bg-client-iconbg flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
                <Icon className='text-client-300 h-5 w-5' />
              </div>
              <div className='min-w-0 flex-1'>
                <p className='text-sm font-semibold text-slate-900'>{paymentLabel(orderPaymentDetails)}</p>
                {type === 'STRIPE' && <p className='text-xs text-slate-500'>Charged after delivery</p>}
              </div>
            </div>
          )}

          {!valid && type !== 'STRIPE' && (
            <p className='text-sm text-slate-500'>
              No payment method on file for this address. Add a card to continue.
            </p>
          )}

          {(cardFormOpen || (!valid && type !== 'ACH' && type !== 'CHECK')) && (
            <div className='rounded-md border border-slate-200 p-3'>
              <p className='mb-1 text-xs font-semibold text-slate-600'>{valid ? 'New card' : 'Add a card'}</p>
              <Stripe
                addressid={addressId}
                cardAdded={() => {
                  setCardFormOpen(false);
                  fetchBilling();
                }}
                cancel={valid ? () => setCardFormOpen(false) : undefined}
              />
            </div>
          )}

          {/* Gift card */}
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-slate-100 pt-3'>
            <GiftIcon className='h-4 w-4 text-slate-400' />
            {giftBalance > 0 ? (
              <p className='text-xs text-slate-600'>
                Gift card balance <span className='font-semibold text-green-600'>{amountCheckFn(giftBalance)}</span>
              </p>
            ) : (
              <p className='text-xs text-slate-500'>Have a gift card?</p>
            )}
            {!giftOpen && <LinkButton onClick={() => setGiftOpen(true)}>Redeem</LinkButton>}
          </div>
          {giftOpen && (
            <div>
              <div className='flex gap-2'>
                <input
                  autoFocus
                  className={`focus:border-client-300 focus:ring-client-300 h-9 flex-1 rounded-md border px-3 text-sm ${
                    giftError ? 'border-red-300' : 'border-slate-200'
                  }`}
                  placeholder='Gift card code'
                  value={giftCode}
                  onChange={(e) => {
                    setGiftCode(e.target.value);
                    setGiftError(null);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && redeemGiftCard()}
                />
                <button
                  type='button'
                  disabled={giftSaving}
                  onClick={redeemGiftCard}
                  className='bg-client-300 hover:bg-client-600 h-9 rounded-md px-4 text-xs font-semibold text-white transition disabled:opacity-50'
                >
                  {giftSaving ? 'Adding…' : 'Add'}
                </button>
                <button
                  type='button'
                  onClick={() => {
                    setGiftOpen(false);
                    setGiftError(null);
                  }}
                  className='h-9 px-2 text-xs font-semibold text-slate-500 hover:text-slate-700'
                >
                  Cancel
                </button>
              </div>
              {giftError && <p className='mt-1 text-xs text-red-600'>{giftError}</p>}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
